import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";
import ServiceTypeFormModal from "./components/ServiceTypeFormModal";
import CancelButton from "components/CancelButton";

function ServiceTypeComponent() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceTypeToDelete, setServiceTypeToDelete] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const [serviceType, setServiceType] = useState({
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    image: null,
    status: "active",
  });

  const initialState = {
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    image: null,
    status: 'active',
  };

  // Fetch sliders with pagination
  const fetchServiceTypes = useCallback(
    async (page, searchValue) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems , search: searchValue };
        const headers = {
        };
        const response = await apiRequest.get(endPoints.GET_SERVICE_TYPES,
          params,
          headers,
        );

        if (response.success) {
          const { items, pagination } = response.data;
          setServiceTypes(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching sliders:", error);
        setError("Failed to fetch sliders. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [ totalItems]
  );

  // Fetch sliders when currentPage changes
  useEffect(() => {
    fetchServiceTypes(currentPage, searchValue);
  }, [currentPage, fetchServiceTypes, searchValue]);

  // Handle modal open/close
  const handleOpenModal = () => {
    setOpenModal(true);
    setServiceType(initialState);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setServiceType(initialState);
    setIsEditing(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setServiceType({ ...serviceType, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Append text fields
      formData.append('name_en', serviceType.name_en);
      formData.append('name_fr', serviceType.name_fr);
      formData.append('description_en', serviceType.description_en);
      formData.append('description_fr', serviceType.description_fr);

      // Append image files
      if (serviceType.image instanceof File) {
        formData.append('image', serviceType.image);
      }
      // Determine the endpoint and method based on whether it's an edit or add operation
      const endPoint = isEditing
        ? `${endPoints.UPDATE_SERVICE_TYPE}/${currentId}`
        : `${endPoints.ADD_SERVICE_TYPE}`;
      const requestMethod = isEditing ? apiRequest.put : apiRequest.post;

      // Send the request
      const response = await requestMethod(endPoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Handle the response
      if (response.success) {
        setSuccess(isEditing ? "Slider updated successfully." : "Slider added successfully.");
        fetchServiceTypes(currentPage); // Refresh the slider list
        handleCloseModal(); // Close the modal
      } else {
        setError(response.message || 'Failed to add/update slider.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while processing your request.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit servict type
  const handleEdit = (serviceType) => {
    handleOpenModal();
    setIsEditing(true);
    setServiceType({
      name_en: serviceType.name_en || "",
      name_fr: serviceType.name_fr || "",
      description_en: serviceType.description_en || "",
      description_fr: serviceType.description_fr || "",
      image: serviceType.image || null,
      image_preview: serviceType.image ? `${apiRequest.BASE_URL}/${serviceType.image}` : null,
      status: serviceType.status || "active",
    });

    setCurrentId(serviceType.id);
  };

  // Handle delete slider
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Send the DELETE request to the API
      const response = await apiRequest.delete(
        `${endPoints.DELETE_SERVICE_TYPE}/${id}`,
        {
          headers: {
          },
        }
      );

      // Handle the response
      if (response.success) {
        setSuccess("Service Type deleted successfully.");

        // Update the local state to remove the deleted slider
        setServiceTypes((prev) => prev.filter((serviceType) => serviceType.id !== id));
      } else {
        setError(response.message || "Failed to delete the service type.");
      }
    } catch (error) {
      console.error("Error deleting service type:", error);
      setError("An error occurred while deleting the service type.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage); // Update the current page
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Update the state with the file and its preview
      setServiceType((prevState) => ({
        ...prevState,
        [fieldName]: file, // Store the file object
        [`${fieldName}_preview`]: URL.createObjectURL(file), // Generate a preview URL
      }));
    } 
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const payload = {
        status: newStatus,
      };

      const headers = {
        'Content-Type': 'application/json',
      };

      // Send the PUT request to update the slider status
      const response = await apiRequest.put(
        `${endPoints.STATUS_SERVICE_TYPE}/${id}`,
        payload,
        { headers }
      );

      // Handle the response
      if (response.success) {
        setSuccess("Service Type status updated successfully.");

        // Update the local state immediately
        setServiceTypes((prevServiceTypes) =>
          prevServiceTypes.map((serviceType) =>
            serviceType.id === id ? { ...serviceType, status: newStatus } : serviceType
          )
        );
      } else {
        setError(response.message || 'Failed to update service type status.');
      }
    } catch (error) {
      console.error('Error updating service type status:', error);
      setError('An error occurred while updating the service type status.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Service Type
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                >
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by title or description"
                    style={{ width: "300px" }}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    Add Service Type
                  </Button>
                </MDBox>
                <TableContainer component={Paper} style={{ marginTop: "1px" }}>
                  <Table>
                    
                    <TableBody>
                    <TableRow>
                        <TableCell style={{ fontWeight: "bold" }}>Name</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Photo</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
                      </TableRow>
                      {serviceTypes.map((serviceType) => (
                        <TableRow key={serviceType.id}>
                          <TableCell>{serviceType.name_en + ' / ' + serviceType.name_fr}</TableCell>
                          <TableCell>
                            <img
                              src={`${apiRequest.BASE_URL}/${serviceType.image}`}
                              alt={serviceType.name_en}
                              style={{ width: "100px", height: "auto" }}
                            />
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={serviceType.status === "active"} // Assuming "active" is the active status
                              onChange={() => handleStatusToggle(serviceType.id, serviceType.status)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={() => handleEdit(serviceType)}
                              color="primary"
                              size="small"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => {
                                setServiceTypeToDelete(serviceType.id); // Set the service type ID to delete
                                setDeleteDialogOpen(true); // Open the confirmation dialog
                              }}
                              color="error"
                              size="small"
                              style={{ marginLeft: "8px" }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages} {/* Display current page and total pages */}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <ServiceTypeFormModal
        open={openModal}
        isEditing={isEditing}
        serviceType={serviceType}
        onClose={handleCloseModal}
        onInputChange={handleInputChange}
        onFileChange={handleFileChange}
        onSubmit={handleSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Service Type</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this service type? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={() => setDeleteDialogOpen(false)} />
          <Button
            onClick={() => {
              setDeleteDialogOpen(false); // Close the dialog
              handleDelete(serviceTypeToDelete); // Proceed with deletion
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Positioning the Snackbar
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ServiceTypeComponent;
